import {Body1, Body2} from '@ui/atoms/typography';
import {FlexRow, FlexRowCenter} from '@ui/style/styles';
import {memo, useState} from 'react';
import {SecondaryPlusIcon} from '@ui/atoms/icons/plus';
import {PadIcon} from '@ui/atoms/icons/pad';
import {HorizontalSideRule, VerticalSpacer} from '@ui/atoms/spacer/spacer';
import {updateFirebaseData} from '@utils/firebase-handler';
import {PenEditIcon} from '@ui/atoms/icons/pen-edit';
import {ArrowDownSecondary} from '@ui/atoms/icons/arrow-down';
import {ArrowUpSecondary} from '@ui/atoms/icons/arrow-up';
import {TrashIcon} from '@ui/atoms/icons/trash';
import {IDashboard, useDashboardHook} from './dashboard-hook';
import {RenameSection} from './rename-section';
import {CreateSection} from './create-section';
import {ItemLoader} from '@ui/organisms/item-loader';
import {WidgetLayout} from './widget-layout';
import {EmptyDashboardState} from './empty-dashboard';
import {CancelIcon, SettingsIcon} from '@ui/atoms/icons';
import {ModalCard} from '@ui/layouts/modal-card';
import {BackAction} from '@ui/molecules/back-action';
import {Modal} from '@ui/molecules/modal';
import {EditInsight} from '../insights/edit-insight/edit-insight';
import {ModalWrapper} from '../insights/styles';
import {DeleteSection} from './delete-section';
import {DashboardSettings} from './dashboard-settings';
import InsightsPage from '../insights';

import DateRangeOverride from './override-date';

interface ReportingDashboardProps {
  dashboardId?: string;
}

const ReportingDashboard = memo(({dashboardId}: ReportingDashboardProps) => {
  const {
    sections,
    updateOrder,
    insights,
    isLoading,
    dashboards,
    renameSection,
    selectedDashboard,
    createSection,
    formatInsight,
    deleteSection,
  } = useDashboardHook(dashboardId);

  const [overallDateRange, setOverallDateRange] = useState<null | {
    startDate: string;
    endDate: string;
  }>(null);

  const [showSettingsModal, setShowSettingsModal] = useState(false);

  const [editInsight, setEditInsight] = useState('');

  const editedInsight = (id: string) => insights?.[id];

  const _insight = editedInsight(editInsight);

  const insight = _insight ? formatInsight(_insight) : null;

  const [showInsight, setShowInsight] = useState(false);

  const [showNewSectionModal, setNewSectionModal] = useState(false);

  const [renameSectionId, setRenameSectionModal] = useState('');

  const [deleteSectionid, setDeleteSectionId] = useState('');

  const [isEditingLayout, setIsEditingLayout] = useState(false);

  const handleAddToDashboard = (
    selectedInsights: string[],
    dashboardId: string,
  ) => {
    selectedInsights.forEach((id) => {
      const insight = editedInsight(id);

      updateFirebaseData(`insights/${insight?.firebaseId}`, {
        ...insight,
        section: 'default',
        dashboard: dashboardId,
      });
    });
  };

  const handleWidgetAdd = async (widgets: any[], slug: string) => {
    widgets.forEach((widget: {firebaseId: string}, index: any) => {
      if (widget.firebaseId) {
        const firebaseId = widget.firebaseId;

        const insight = insights[firebaseId];

        if (insight && insight.section !== slug) {
          updateFirebaseData(`insights/${firebaseId}`, {
            ...insight,
            orderIndex: index,
            section: slug,
          });
        } else {
          updateFirebaseData(`insights/${firebaseId}`, {
            ...insight,
            orderIndex: index,
          });
        }
      }
    });
  };

  const isEmpty =
    !sections.length || sections.every((section) => !section.widget.length);

  return (
    <div className="mt-6">
      <div className="bg-white rounded-[10px] sm:p-4 p-6 border border-borderLight h-full gap-4">
        {isEmpty && !isLoading ? (
          <EmptyDashboardState
            toggleSettings={() => setShowSettingsModal(true)}
            toggleInsight={() => setShowInsight(true)}
          />
        ) : null}

        {!isEmpty ? (
          <div className="flex justify-between items-center pb-4">
            <DateRangeOverride
              onDateRangeChange={(newRange) => {
                setOverallDateRange(newRange as any);
              }}
            />

            <div className="flex flex-row flex-wrap justify-end gap-3 mr-0 sm:mr-3">
              {isEditingLayout ? (
                <button onClick={() => setNewSectionModal(true)}>
                  <FlexRow className="gap-2 justify-center sm:justify-start">
                    <SecondaryPlusIcon />
                    <Body2 weight="semibold" kind="purple300">
                      New section
                    </Body2>
                  </FlexRow>
                </button>
              ) : (
                <button onClick={() => setShowInsight(true)} className="">
                  <FlexRow className="gap-2 justify-center sm:justify-start">
                    <SecondaryPlusIcon />
                    <Body2 weight="semibold" kind="purple300">
                      Add Insight
                    </Body2>
                  </FlexRow>
                </button>
              )}

              <HorizontalSideRule className="sm:hidden block" size="20px" />

              <button onClick={() => setIsEditingLayout(!isEditingLayout)}>
                <FlexRow className="gap-2 justify-center sm:justify-start">
                  <PadIcon />
                  <Body2 weight="semibold" kind="purple300">
                    {isEditingLayout ? 'Exit layout edit' : 'Edit layout'}
                  </Body2>
                </FlexRow>
              </button>

              <HorizontalSideRule className="sm:hidden block" size="20px" />

              <button onClick={() => setShowSettingsModal(true)}>
                <FlexRow className="gap-2 justify-center sm:justify-start">
                  <SettingsIcon />
                  <Body2 weight="semibold" kind="purple300">
                    Settings
                  </Body2>
                </FlexRow>
              </button>
            </div>
          </div>
        ) : null}

        {!sections.length && isLoading && (
          <FlexRowCenter className="min-h-[200px]">
            <ItemLoader />
          </FlexRowCenter>
        )}

        {!isLoading &&
          !isEmpty &&
          sections.map((section) => (
            <div key={section.name}>
              <div className=" bg-white border border-borderLight pt-4 pb-6 sm:px-4 px-6 mb-6 rounded-[10px]">
                <div className="flex sm:flex-col flex-row justify-between sm:items-start items-center gap-4 mb-6 sm:gap-0">
                  <FlexRow className="sm:mb-4 mb-0 gap-2">
                    <Body1 weight="bold">{section.name}</Body1>
                    {isEditingLayout ? (
                      <button
                        className="mb-1"
                        onClick={() =>
                          setRenameSectionModal(section.firebaseId)
                        }>
                        <PenEditIcon />
                      </button>
                    ) : null}
                  </FlexRow>

                  {isEditingLayout && (
                    <FlexRow className="gap-4 justify-end">
                      <FlexRow className="gap-1">
                        <button
                          onClick={() =>
                            updateOrder(
                              section.order + 1,
                              section.firebaseId,
                              dashboardId || '',
                            )
                          }>
                          <ArrowDownSecondary />
                        </button>
                        <button
                          onClick={() =>
                            updateOrder(
                              section.order - 1,
                              section.firebaseId,
                              dashboardId || '',
                            )
                          }>
                          <ArrowUpSecondary />
                        </button>
                      </FlexRow>
                      <button
                        disabled={!section.canDelete}
                        onClick={() => setDeleteSectionId(section.firebaseId)}>
                        <TrashIcon
                          fill={section.canDelete ? '#D52A2A' : '#BFBFD4'}
                        />
                      </button>
                    </FlexRow>
                  )}
                </div>

                <div>
                  <WidgetLayout
                    handleEditLayout={setEditInsight}
                    isEditing={isEditingLayout}
                    overallDateRange={overallDateRange}
                    onAdd={(widgets) => handleWidgetAdd(widgets, section.slug)}
                    onRemove={() => {}}
                    sectionId={section.firebaseId}
                    widget={section.widget as any}
                  />
                </div>
              </div>
            </div>
          ))}
      </div>

      <Modal open={!!editInsight} onClose={() => setEditInsight('')}>
        <ModalWrapper>
          <BackAction
            icon={<CancelIcon />}
            title="Close"
            onClick={() => setEditInsight('')}
            width="md"
          />
          <VerticalSpacer size="16px" />

          <ModalCard title="" modalStyle={{padding: 0, overflow: 'hidden'}}>
            <div className="overflow-scroll h-[80vh]">
              {insight && (
                <EditInsight
                  {...insight}
                  handleAddToDashboard={handleAddToDashboard}
                  dashboards={dashboards}
                  dashboard={dashboardId}
                  onClose={() => setEditInsight('')}
                  onDuplicate={() => {}}
                  firebaseId={insight.firebaseId || ''}
                />
              )}
            </div>
          </ModalCard>
        </ModalWrapper>
      </Modal>

      {!!renameSectionId && (
        <RenameSection
          handleRenameSection={renameSection}
          defaultTitle={
            sections.find((section) => section.firebaseId === renameSectionId)
              ?.name
          }
          renameSectionId={renameSectionId}
          setRenameSectionModal={setRenameSectionModal}
        />
      )}

      {showNewSectionModal && (
        <CreateSection
          handleCreateSection={(data) =>
            dashboardId ? createSection(dashboardId, data) : null
          }
          setNewSectionModal={setNewSectionModal}
          showNewSectionModal={showNewSectionModal}
        />
      )}

      {showInsight && (
        <InsightModal
          onClose={() => setShowInsight(false)}
          dashboards={dashboards}
        />
      )}

      {showSettingsModal && dashboardId && (
        <DashboardSettings
          isOpen={showSettingsModal}
          handleClose={() => setShowSettingsModal(false)}
          dashboardId={dashboardId}
        />
      )}

      {deleteSectionid && (
        <DeleteSection
          onClose={() => setDeleteSectionId('')}
          topic={selectedDashboard?.sections?.[deleteSectionid]?.name}
          handleDelete={() => {
            deleteSection(deleteSectionid);

            setDeleteSectionId('');
          }}
        />
      )}
    </div>
  );
});

export default ReportingDashboard;

interface InsightModalProps {
  onClose: () => void;
  dashboards: IDashboard[];
}
const InsightModal = ({onClose, dashboards}: InsightModalProps) => {
  return (
    <Modal open={true} onClose={() => onClose()}>
      <ModalWrapper>
        <div className="flex justify-end mb-3">
          <BackAction
            icon={<CancelIcon />}
            title="Close"
            onClick={() => onClose()}
            width="md"
          />
        </div>
        <ModalCard title="" modalStyle={{padding: 0, overflow: 'hidden'}}>
          <div className="overflow-scroll h-[80vh]" id="insight-modal">
            <InsightsPage dashboards={dashboards} />
          </div>
        </ModalCard>
      </ModalWrapper>
    </Modal>
  );
};
